// PWA Install Hook - handles install prompts for Android/iOS
import { useState, useEffect, useCallback } from 'react';
import { storage } from '../services/storage';

// BeforeInstallPromptEvent interface (not in standard TypeScript types)
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAInstallState {
  // Platform detection
  isIOS: boolean;
  isAndroid: boolean;

  // Installation state
  isInstalled: boolean;
  canInstallNatively: boolean;

  // Prompt visibility
  shouldShowPrompt: boolean;

  // Actions
  promptInstall: () => Promise<{ outcome: 'accepted' | 'dismissed' } | null>;
  dismissPrompt: () => void;
}

const PROMPT_DISMISSED_KEY = 'shopman-pwa-prompt-dismissed';
const PROMPT_COOLDOWN_DAYS = 7; // Days to wait before showing again after dismiss

// Modul-niveau detektorer (react-hooks strict-rules-refaktor 2026-06-11):
// bruges som lazy useState-initializers i stedet for setState-i-effekt.

/** Check if running as installed PWA. */
function detectInstalled(): boolean {
  if (typeof window === 'undefined') return false;

  // iOS standalone mode
  if ((navigator as unknown as { standalone?: boolean }).standalone === true) return true;

  // Display mode media query (works on most platforms)
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  if (window.matchMedia('(display-mode: minimal-ui)').matches) return true;

  return false;
}

/** Check if prompt was dismissed within the cooldown window. */
function readRecentlyDismissed(): boolean {
  const dismissedAt = storage.get(PROMPT_DISMISSED_KEY);
  if (!dismissedAt) return false;

  const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
  return daysSince < PROMPT_COOLDOWN_DAYS;
}

/** Check if iOS Safari (not Chrome/Firefox on iOS). */
function detectIOSSafari(isIOS: boolean): boolean {
  if (!isIOS) return false;
  const ua = navigator.userAgent;
  return /WebKit/.test(ua) && !/CriOS|FxiOS/.test(ua);
}

export function usePWAInstall(): PWAInstallState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(detectInstalled);
  const [recentlyDismissed, setRecentlyDismissed] = useState(readRecentlyDismissed);

  // Platform detection
  const isIOS = typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream;

  const isAndroid = typeof navigator !== 'undefined' &&
    /Android/.test(navigator.userAgent);

  // Handle beforeinstallprompt event (Android/Chrome)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome's mini-infobar
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Afledt — ikke state. Vis prompten når: ikke installeret, ikke afvist
  // for nylig, og enten native install er mulig (Android/Chrome) eller vi
  // er i iOS Safari (manuel "Føj til hjemmeskærm"-vejledning).
  const shouldShowPrompt =
    !isInstalled &&
    !recentlyDismissed &&
    (deferredPrompt !== null || detectIOSSafari(isIOS));

  // Trigger native install prompt (Android/Chrome)
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;

    const result = await deferredPrompt.prompt();

    if (result.outcome === 'accepted') {
      setDeferredPrompt(null);
    }

    return result;
  }, [deferredPrompt]);

  // Dismiss prompt
  const dismissPrompt = useCallback(() => {
    storage.set(PROMPT_DISMISSED_KEY, Date.now().toString());
    setRecentlyDismissed(true);
  }, []);

  return {
    isIOS,
    isAndroid,
    isInstalled,
    canInstallNatively: deferredPrompt !== null,
    shouldShowPrompt,
    promptInstall,
    dismissPrompt,
  };
}

export default usePWAInstall;
